import React, { useMemo, useState } from "react";
import { ShotMap, Unavailable } from "./FootballPitch";
import {
  bodyPartLabel,
  contextLabel,
  countBy,
  distinctValues,
  filterShots,
  formatNumber,
  outcomeLabel,
  summariseShots,
} from "./fiData";
import "./fi-extra.css";

const signed = (value) => (typeof value !== "number" ? "—" : `${value > 0 ? "+" : ""}${value.toFixed(2)}`);

/**
 * Shot map with summary numbers and filters. Everything shown is a count or
 * sum of the provider's located shots; no shot-on-target claim is made because
 * the provider's on-target flags are unreconciled.
 */
export default function ShotExplorer({ shots = [], matchOptions = [], heading }) {
  const [filters, setFilters] = useState({});
  const [half, setHalf] = useState(true);

  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value || undefined }));

  const filtered = useMemo(() => filterShots(shots, filters), [shots, filters]);
  const summary = useMemo(() => summariseShots(filtered), [filtered]);

  // Chip counts ignore the chip's own filter so users can see what switching would give.
  const withoutFilter = (field) => filterShots(shots, { ...filters, [field]: undefined });
  const outcomeCounts = countBy(withoutFilter("outcome"), "outcome", (v) => v);
  const outcomes = distinctValues(shots, "outcome");
  const bodyParts = distinctValues(shots, "body_part");
  const contexts = distinctValues(shots, "context");
  const shotMatches = matchOptions.filter((m) => shots.some((s) => String(s.fixture_id) === String(m.fixture_id)));
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const countFor = (value) => outcomeCounts.find(([key]) => key === value)?.[1] || 0;

  if (!shots.length) return <Unavailable text="No located shots in the selected sample." />;

  return (
    <div>
      {heading && <p className="fx-sub">{heading}</p>}
      <div className="fx-tiles">
        <div className="fx-tile"><span>Shots</span><strong>{summary.count}</strong><small>{summary.inBox} inside the box</small></div>
        <div className="fx-tile"><span>Goals</span><strong>{summary.goals}</strong><small>from located shots</small></div>
        <div className="fx-tile"><span>Total xG</span><strong>{formatNumber(summary.xg)}</strong><small>source xG, summed</small></div>
        <div className="fx-tile"><span>xG per shot</span><strong>{formatNumber(summary.xgPerShot)}</strong><small>mean chance quality</small></div>
        <div className="fx-tile"><span>Goals − xG</span><strong>{signed(summary.goalsMinusXg)}</strong><small>finishing vs chance quality</small></div>
      </div>

      <div className="fx-toolbar">
        <div className="fx-chips" role="group" aria-label="Filter by outcome">
          <button type="button" className="fx-chip" aria-pressed={!filters.outcome} onClick={() => setFilter("outcome", "")}>
            All<small>{outcomeCounts.reduce((n, [, c]) => n + c, 0)}</small>
          </button>
          {outcomes.map((outcome) => (
            <button
              key={outcome}
              type="button"
              className="fx-chip"
              aria-pressed={filters.outcome === outcome}
              disabled={!countFor(outcome) && filters.outcome !== outcome}
              onClick={() => setFilter("outcome", filters.outcome === outcome ? "" : outcome)}
            >
              {outcomeLabel(outcome)}<small>{countFor(outcome)}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="fx-toolbar">
        {contexts.length > 1 && (
          <label className="fx-field">Situation
            <select value={filters.context || ""} onChange={(e) => setFilter("context", e.target.value)}>
              <option value="">All situations</option>
              {contexts.map((c) => <option key={c} value={c}>{contextLabel(c)}</option>)}
            </select>
          </label>
        )}
        {bodyParts.length > 1 && (
          <label className="fx-field">Body part
            <select value={filters.body_part || ""} onChange={(e) => setFilter("body_part", e.target.value)}>
              <option value="">Any</option>
              {bodyParts.map((b) => <option key={b} value={b}>{bodyPartLabel(b)}</option>)}
            </select>
          </label>
        )}
        {shotMatches.length > 1 && (
          <label className="fx-field">Match
            <select value={filters.fixture_id || ""} onChange={(e) => setFilter("fixture_id", e.target.value)}>
              <option value="">All {shotMatches.length} matches</option>
              {shotMatches.map((m) => <option key={m.fixture_id} value={m.fixture_id}>{m.name}</option>)}
            </select>
          </label>
        )}
        <label className="fx-check"><input type="checkbox" checked={!!filters.boxOnly} onChange={(e) => setFilter("boxOnly", e.target.checked)} />Inside the box only</label>
        <label className="fx-check"><input type="checkbox" checked={half} onChange={(e) => setHalf(e.target.checked)} />Attacking half</label>
        {activeFilterCount > 0 && <button type="button" className="fx-button" onClick={() => setFilters({})}>Reset filters</button>}
      </div>

      {filtered.length ? (
        <div className="fx-two" style={{ margin: 0 }}>
          <ShotMap shots={filtered} half={half} />
          <div className="fx-stack">
            <BreakdownTable title="Situation" rows={breakdown(filtered, "context", contextLabel)} />
            <BreakdownTable title="Body part" rows={breakdown(filtered, "body_part", bodyPartLabel)} />
          </div>
        </div>
      ) : (
        <p className="fx-callout">No shots match these filters. Reset them to see all {shots.length} located shots.</p>
      )}
    </div>
  );
}

function breakdown(shots, field, labeller) {
  const groups = new Map();
  shots.forEach((shot) => {
    const key = labeller(shot[field]);
    const row = groups.get(key) || { label: key, shots: 0, goals: 0, xg: 0 };
    row.shots += 1;
    if (shot.outcome === "Goal") row.goals += 1;
    if (typeof shot.xg === "number") row.xg += shot.xg;
    groups.set(key, row);
  });
  return [...groups.values()].sort((a, b) => b.shots - a.shots);
}

function BreakdownTable({ title, rows }) {
  return (
    <div className="fx-scroll" style={{ maxHeight: "none" }}>
      <table>
        <thead><tr><th>{title}</th><th className="fx-num">Shots</th><th className="fx-num">Goals</th><th className="fx-num">xG</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <td>{row.label}</td>
              <td className="fx-num">{row.shots}</td>
              <td className="fx-num">{row.goals}</td>
              <td className="fx-num">{row.xg.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
